import * as React from 'react'
import {
  FetchNextPageOptions,
  InfiniteData,
  InfiniteQueryObserverResult,
} from '@tanstack/react-query'

type TUseInfinityLoadMoreProps<T> = {
  hasNextPage: boolean
  isFetchingNextPage: boolean
  fetchNextPage: (
    options?: FetchNextPageOptions | undefined
  ) => Promise<InfiniteQueryObserverResult<InfiniteData<T, unknown>, Error>>
}

type TUseInfinityLoadMore = {
  scrollContainerRef: React.RefObject<HTMLDivElement>
  loadMoreRef: React.RefObject<HTMLDivElement>
}

export const useInfinityLoadMore = <T>(
  props: TUseInfinityLoadMoreProps<T>
): TUseInfinityLoadMore => {
  const { hasNextPage, isFetchingNextPage, fetchNextPage } = props

  const scrollContainerRef = React.useRef<HTMLDivElement>(null)
  const loadMoreRef = React.useRef<HTMLDivElement>(null)

  // Handle intersection observer for infinite scroll
  const stateRef = React.useRef({ hasNextPage, isFetchingNextPage })

  // Update ref without triggering re-renders
  React.useEffect(() => {
    stateRef.current = { hasNextPage, isFetchingNextPage }
  }, [hasNextPage, isFetchingNextPage])

  const handleIntersection = React.useCallback(
    (entries: IntersectionObserverEntry[]) => {
      const entry = entries[0]
      const { hasNextPage, isFetchingNextPage } = stateRef.current

      if (entry?.isIntersecting && hasNextPage && !isFetchingNextPage) {
        fetchNextPage()
      }
    },
    [fetchNextPage]
  )

  // Set up IntersectionObserver only once
  React.useEffect(() => {
    const targetElement = loadMoreRef.current
    const rootElement = scrollContainerRef.current

    if (!targetElement) return

    const observer = new IntersectionObserver(handleIntersection, {
      root: rootElement || null,
      rootMargin: '100px',
      threshold: 0.1,
    })

    observer.observe(targetElement)

    return () => {
      observer.disconnect()
    }
  }, [handleIntersection, hasNextPage, isFetchingNextPage])

  return React.useMemo(
    () => ({ scrollContainerRef, loadMoreRef }),
    [scrollContainerRef, loadMoreRef]
  )
}
